import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { ClientActivity } from '../types/client';
import { ClientService } from '../services/clientService';

interface ActivityTimelineProps {
  clientId: number;
}

const ACTIVITY_CONFIG: Record<string, { icon: React.ReactElement; color: string; label: string }> = {
  doc_sent_email: { icon: <EmailIcon />, color: '#1976d2', label: 'Email Sent' },
  doc_sent_whatsapp: { icon: <WhatsAppIcon />, color: '#25D366', label: 'WhatsApp Sent' },
  doc_sent_sms: { icon: <SmsIcon />, color: '#f57c00', label: 'SMS Sent' },
  interest_added: { icon: <StarIcon />, color: '#ffc107', label: 'Interest Added' },
  interest_removed: { icon: <StarBorderIcon />, color: '#9e9e9e', label: 'Interest Removed' },
  query_asked: { icon: <SearchIcon />, color: '#9c27b0', label: 'Query Asked' },
  doc_viewed: { icon: <ViewIcon />, color: '#607d8b', label: 'Document Viewed' },
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ clientId }) => {
  const [activities, setActivities] = useState<ClientActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await ClientService.getClientActivity(clientId);
      setActivities(data);
    } catch (err) {
      console.error('Failed to load activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = filter === 'all'
    ? activities
    : activities.filter(a => a.activity_type === filter);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActivityDetails = (activity: ClientActivity): string => {
    const details = activity.details || {};
    if (details.doc_title) return details.doc_title;
    if (details.sector_name) return details.sector_name;
    if (details.company_name) return details.company_name;
    if (details.query) return details.query;
    if (details.channel) return `via ${details.channel}`;
    return '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HistoryIcon fontSize="small" />
          Activity Timeline
        </Typography>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="doc_sent_email">Email</MenuItem>
            <MenuItem value="doc_sent_whatsapp">WhatsApp</MenuItem>
            <MenuItem value="doc_sent_sms">SMS</MenuItem>
            <MenuItem value="interest_added">Interests</MenuItem>
            <MenuItem value="query_asked">Queries</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredActivities.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
          No activity recorded yet
        </Typography>
      ) : (
        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
          {filteredActivities.map((activity) => {
            const config = ACTIVITY_CONFIG[activity.activity_type] || {
              icon: <HistoryIcon />,
              color: '#757575',
              label: activity.activity_type,
            };
            return (
              <ListItem key={activity.id} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36, color: config.color }}>
                  {config.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={config.label} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {getActivityDetails(activity)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="textSecondary">
                      {formatTime(activity.created_at)}
                      {activity.created_by && activity.created_by !== 'system' && ` by ${activity.created_by}`}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ActivityTimeline;
